import React, { useMemo } from "react";
import { CalendarPlus, Check, Clock, MapPin } from "lucide-react";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Relative on purpose: pure modules, resolvable under Jest without a shim.
import { resolveApiUrl } from "../../pathways/learnerApi";
import { browserTimeZone, sessionTimeLabel } from "../../pathways/learnerModel";
import { formatMoneyCode } from "../../lib/formatMoney";
import { tenantHref } from "../../lib/tenantPath";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";
import GatedLink from "./GatedLink";

const PRIMARY = "inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-eh-ink px-6 text-sm font-semibold text-eh-cream hover:bg-eh-ink/90";
const PRIMARY_DISABLED = "inline-flex h-11 w-full cursor-not-allowed items-center justify-center gap-2 rounded-full bg-eh-ink px-6 text-sm font-semibold text-eh-cream opacity-50";
const SECONDARY = "inline-flex h-11 items-center justify-center gap-2 rounded-full border border-eh-border px-5 text-sm font-medium text-eh-ink hover:bg-eh-cream-deep";
const SECONDARY_DISABLED = "inline-flex h-11 cursor-not-allowed items-center justify-center gap-2 rounded-full border border-eh-border px-5 text-sm font-medium text-eh-ink opacity-50";

function LocationNote({ note }) {
  if (!note || !note.trim()) return null;
  return (
    <p data-testid={IDS.liveLocation} className="flex items-start gap-2 text-sm text-eh-ink">
      <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-eh-muted" aria-hidden="true" />
      <span className="min-w-0 break-words">{note}</span>
    </p>
  );
}

/**
 * Cohort: the session's instant in the browser's zone, the zone named, the length, the
 * location note and "Add to calendar" (the ICS, contract §10.4). The practitioner ticks
 * attendance after the session, and that completes the step: "Attended ✓" then.
 */
function CohortSession({ live, disabled, timeZone }) {
  const t = useT("public");
  const { lang } = useLanguage();
  const when = sessionTimeLabel(live.starts_at, lang, timeZone);
  return (
    <>
      {when ? (
        <div className="rounded-2xl bg-eh-surface px-4 py-4">
          <p data-testid={IDS.liveWhen} className="font-semibold text-eh-ink">{when}</p>
          <p data-testid={IDS.liveZone} className="mt-1 text-xs text-eh-muted">{t("pathways.live.zone", { zone: timeZone })}</p>
          {live.duration_minutes > 0 && (
            <p data-testid={IDS.liveDuration} className="mt-2 inline-flex items-center gap-1.5 text-sm text-eh-ink">
              <Clock className="h-4 w-4 text-eh-muted" aria-hidden="true" />
              {t("pathways.live.duration", { n: live.duration_minutes })}
            </p>
          )}
        </div>
      ) : (
        <p data-testid={IDS.liveWhen} className="text-sm text-eh-muted">{t("pathways.live.timeTbd")}</p>
      )}
      <LocationNote note={live.location_note} />
      {live.attended ? (
        <p data-testid={IDS.liveAttended} className="inline-flex items-center gap-1.5 rounded-full bg-eh-sage-soft px-3 py-1 text-sm font-medium text-eh-ink">
          {t("pathways.live.attended")}
        </p>
      ) : (
        <>
          {when && live.calendar_url && (
            <GatedLink
              href={resolveApiUrl(live.calendar_url)}
              download
              disabled={disabled}
              testId={IDS.liveCalendar}
              className={SECONDARY}
              disabledClassName={SECONDARY_DISABLED}
            >
              <CalendarPlus className="h-4 w-4" aria-hidden="true" />
              {t("pathways.live.addToCalendar")}
            </GatedLink>
          )}
          <p data-testid={IDS.liveCompletesHint} className="text-xs text-eh-muted">{t("pathways.live.attendanceHint")}</p>
        </>
      )}
    </>
  );
}

/**
 * Self-paced: the linked session type, booked separately on the practice's booking page
 * (`booking_path`, contract §10.7, which preselects that service). The step completes when
 * a booking of it is marked complete (§10.4):
 *
 *   done                "Session on <date> completed" (the booking that completed it)
 *   upcoming booking    "Booked for <date>"
 *   otherwise           "Book this session", when the service still exists and is active
 *   always, until done  "Completes when your session is marked complete"
 */
function SelfPacedSession({ live, done, disabled, timeZone }) {
  const t = useT("public");
  const { lang } = useLanguage();
  const service = live.service;
  const bookable = Boolean(service && service.active);
  return (
    <>
      {service && (
        <div data-testid={IDS.liveService} className="rounded-2xl bg-eh-surface px-4 py-4">
          <p className="font-semibold text-eh-ink break-words">{service.name}</p>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-eh-muted">
            {service.duration_min > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4" aria-hidden="true" />
                {t("pathways.live.duration", { n: service.duration_min })}
              </span>
            )}
            <span>{service.price_cents > 0 ? formatMoneyCode(service.price_cents, service.currency) : t("pathways.live.free")}</span>
          </p>
        </div>
      )}
      <LocationNote note={live.location_note} />

      {done && live.completed_booking && (
        <p data-testid={IDS.liveCompletedBooking} className="inline-flex items-center gap-1.5 rounded-full bg-eh-sage-soft px-3 py-1 text-sm font-medium text-eh-ink">
          <Check className="h-4 w-4" aria-hidden="true" />
          {t("pathways.live.completedSession", { when: sessionTimeLabel(live.completed_booking.slot_start, lang, timeZone) })}
        </p>
      )}
      {!done && live.upcoming && (
        <p data-testid={IDS.liveUpcoming} className="rounded-2xl bg-eh-ochre/10 px-4 py-3 text-sm font-medium text-eh-ink">
          {t("pathways.live.bookedFor", { when: sessionTimeLabel(live.upcoming.slot_start, lang, timeZone) })}
        </p>
      )}
      {!done && !live.upcoming && bookable && (
        <GatedLink
          href={tenantHref(live.booking_path)}
          disabled={disabled}
          testId={IDS.liveBook}
          className={PRIMARY}
          disabledClassName={PRIMARY_DISABLED}
        >
          {t("pathways.live.book")}
        </GatedLink>
      )}
      {!done && !live.upcoming && !bookable && (
        <p data-testid={IDS.liveServiceUnavailable} className="text-sm text-eh-muted">{t("pathways.live.serviceUnavailable")}</p>
      )}
      {!done && (
        <p data-testid={IDS.liveCompletesHint} className="text-xs text-eh-muted">{t("pathways.live.completesOnBooking")}</p>
      )}
    </>
  );
}

/**
 * A live step's screen (contract §10.0.3, §10.0.4, §10.7, §10.11). Nothing here completes
 * the step: a cohort step completes on the practitioner's attendance tick, a self-paced
 * one on a completed booking. Every time is shown in the BROWSER's zone, which is named.
 *
 * `disabled` (the admin preview): "Add to calendar" and "Book this session" are shown but
 * not followable.
 *
 * @param {object} props
 * @param {object} props.live      MemberStepScreen.live
 * @param {boolean} props.done     the step is completed
 * @param {boolean} [props.disabled=false]
 */
export default function StepLive({ live, done, disabled = false }) {
  const timeZone = useMemo(() => browserTimeZone(), []);
  return (
    <div data-testid={IDS.live} data-mode={live.mode} className="space-y-3">
      {live.mode === "cohort"
        ? <CohortSession live={live} disabled={disabled} timeZone={timeZone} />
        : <SelfPacedSession live={live} done={done} disabled={disabled} timeZone={timeZone} />}
    </div>
  );
}
